import { createClient } from "@supabase/supabase-js";
import type { Database } from './types';
import { storageBackup } from './storage-backup';
import { logger } from '@/lib/utils/logger';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing Supabase environment variables");
}

// Validate that we're not using insecure URLs in production
if (import.meta.env.PROD && (supabaseUrl.startsWith('http://') || supabaseUrl.includes('localhost') || supabaseUrl.includes('127.0.0.1'))) {
  logger.error('Insecure Supabase URL detected in production:', supabaseUrl);
  throw new Error("Insecure Supabase URL detected in production environment");
}

const isServer = typeof window === 'undefined';

// Track failed refresh attempts to prevent cascades
const failedRefreshCount = 0;
const lastFailedRefreshTime = 0;
const permanentlyBlockRefresh = false;

// Track 400 errors to prevent retry loops
let consecutive400Errors = 0;
let lastErrorTime = 0;

// Custom fetch that intercepts 400 errors on auth endpoints
const customFetch = async (url: RequestInfo | URL, options?: RequestInit): Promise<Response> => {
  try {
    const response = await fetch(url, options);
    
    // Check if this is an auth token refresh that failed
    if (response.status === 400 && url.toString().includes('/auth/v1/token')) {
      const now = Date.now();
      
      // Reset counter if it's been more than 5 seconds since last error
      if (now - lastErrorTime > 5000) {
        consecutive400Errors = 0;
      }
      
      consecutive400Errors++;
      lastErrorTime = now;
      
      logger.warn(`Token refresh failed with 400 (${consecutive400Errors} consecutive errors)`);
      
      // If we get 2 or more 400 errors in quick succession, clear the session
      if (consecutive400Errors >= 2) {
        logger.error('Multiple token refresh failures detected, clearing session to prevent loops');
        
        // Clear auth tokens from localStorage
        const authKey = `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token`;
        localStorage.removeItem(authKey);
        
        // Reset error counter
        consecutive400Errors = 0;
        
        // Return a response that will make Supabase think the user is logged out
        return new Response(JSON.stringify({ error: 'Invalid refresh token' }), {
          status: 400,
          headers: { 'Content-Type': 'application/json' }
        });
      }
    } else if (response.ok) {
      // Reset counter on successful requests
      consecutive400Errors = 0;
    }
    
    return response;
  } catch (error) {
    logger.error('Fetch error:', error);
    throw error;
  }
};

// Create a custom storage implementation that handles errors gracefully
const storage = {
  getItem: async (key: string): Promise<string | null> => {
    try {
      const value = localStorage.getItem(key);
      if (import.meta.env.DEV) {
        logger.debug(`Supabase storage getItem(${key}):`, value ? 'found' : 'null');
      }
      
      // Add stack trace when session is null to see what's calling it
      if (!value && key.includes('auth-token')) {
        if (import.meta.env.DEV) logger.debug('Session is null, attempting recovery...');
        
        // Try multi-level recovery
        const recoveredValue = await storageBackup.restore(key);
        if (recoveredValue) {
          try {
            // Verify it's valid JSON before restoring
            JSON.parse(recoveredValue);
            localStorage.setItem(key, recoveredValue);
            if (import.meta.env.DEV) logger.info('Session restored to localStorage from backup');
            return recoveredValue;
          } catch (e) {
            logger.error('Failed to restore session - invalid JSON:', e);
          }
        } else {
          if (import.meta.env.DEV) logger.debug('No backup found in any storage layer');
          
          // If this is the main auth token request and we have no backup, 
          // it might mean the app just started - give it time
          if (key === 'sb-pmtawanclqujznxnbhng-auth-token' && import.meta.env.DEV) {
            logger.debug('Auth token missing - possible fresh start or cleared storage');
          }
        }
      }
      
      // Temporarily disable aggressive validation - just log what we find
      if (value && key.includes('auth-token') && import.meta.env.DEV) {
        try {
          const parsed = JSON.parse(value);
          logger.debug(`Session data structure for key ${key}:`, Object.keys(parsed));
          // DISABLED: Don't remove sessions even if they seem invalid
          // Let Supabase handle session validation
        } catch (e) {
          logger.warn(`Corrupted session data in localStorage for key ${key}`, e);
          // DON'T remove the session - let Supabase handle it
          // localStorage.removeItem(key);
          // return null;
        }
      }
      return value;
    } catch (error) {
      logger.error('Error reading from localStorage:', error);
      
      // Try multi-level recovery as fallback
      try {
        const backupValue = await storageBackup.restore(key);
        if (backupValue && key.includes('auth-token')) {
          if (import.meta.env.DEV) logger.info('Using backup due to localStorage error');
          return backupValue;
        }
      } catch (e) {
        logger.error('Backup recovery also failed:', e);
      }
      
      return null;
    }
  },
  setItem: async (key: string, value: string): Promise<void> => {
    try {
      if (import.meta.env.DEV) {
        logger.debug(`Supabase storage setItem(${key}):`, value ? 'setting value' : 'null value');
      }
      
      // Temporarily disable validation on setItem too
      if (key.includes('auth-token') && value) {
        try {
          const parsed = JSON.parse(value);
          if (import.meta.env.DEV) logger.debug(`Storing session data with keys:`, Object.keys(parsed));
          
          // Create multi-level backup
          await storageBackup.backup(key, value);
        } catch (e) {
          logger.warn(`Invalid JSON for session data for key ${key}`, e);
          return;
        }
      }
      localStorage.setItem(key, value);
      if (import.meta.env.DEV) logger.debug(`Supabase storage setItem(${key}): success`);
    } catch (error) {
      logger.error('Error writing to localStorage:', error);
      
      // Try backup system as fallback
      if (key.includes('auth-token')) {
        try {
          await storageBackup.backup(key, value);
          if (import.meta.env.DEV) logger.info('Session saved to backup system as fallback');
        } catch (e) {
          logger.error('Backup system also failed:', e);
        }
      }
    }
  },
  removeItem: async (key: string): Promise<void> => {
    try {
      if (import.meta.env.DEV) logger.debug(`Supabase storage removeItem(${key})`);
      // Add stack trace to see what's calling removeItem
      if (key.includes('auth-token') && import.meta.env.DEV) {
        logger.debug('Session removal stack trace:', new Error().stack);
      }
      localStorage.removeItem(key);
      // Note: We don't remove from backup storage - let it expire naturally
      // This helps with recovery if localStorage is cleared unexpectedly
    } catch (error) {
      logger.error('Error removing from localStorage:', error);
    }
  }
};

// Create the Supabase client with standard configuration
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Add a storage key prefix to avoid conflicts
    storageKey: 'sb-auth',
    storage
  },
  global: {
    headers: {
      // Custom headers for tracking
      "X-Client-Info": "rendezvous-web",
    },
    fetch: customFetch,
  },
  db: {
    schema: 'public'
  }
});

// Auth state listener removed - consolidated into auth-manager.ts
// This prevents duplicate listeners and race conditions

// Add debug logging for auth state changes
if (import.meta.env.DEV) {
  supabase.auth.onAuthStateChange((event, session) => {
    logger.debug('Supabase auth event:', event, session?.user?.email);
  });
}
